import React from 'react';
import { Table, Space, Button, Tooltip, Tag, Select, Spin } from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
  InfoCircleFilled,
  CheckOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
  DollarOutlined,
  CarOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { hasSchedulingIssue, extractTimeFromOrder, formatPrice, getStatusColor } from '../../../utils/orderUtils';
import OrderStatusCell from './OrderStatusCell';
import OrderTaskNoteButton from './OrderTaskNoteButton';
import usePermission from '../../../hooks/usePermission';
import { blockChangeStatus } from '../../../utils/orderUtils';
import moment from 'moment';
import { getOnfleetImageUrls, getOrderCompletionDetails, getOrderCompletionTime, getTrackingInfo } from '../../../utils/onfleetUtils';
import { showOnfleetCompletionModal } from './OnfleetCompletionModal';
import {
  canCaptureSavedCard as isSavedCardCaptureEligible,
  getSavedCardActionLabel,
  getSavedCardActionTooltip,
} from '../../../utils/orderPaymentAction';

const { Option } = Select;

const ORDER_TABLE_COLUMN_WIDTHS = Object.freeze({
  orderId: 100,
  property: 120,
  customer: 160,
  address: 240,
  date: 100,
  time: 120,
  total: 100,
  status: 112,
  payment: 80,
  onfleet: 160,
  actions: 220
});

const ORDER_SELECTION_COLUMN_WIDTH = 44;

/**
 * Component for displaying orders in a table format with scheduling priority
 */
const OrderTable = ({
  orders = [],
  loading = false,
  pagination = {},
  sortInfo = {},
  onTableChange,
  isAdmin = false,
  selectedRowKeys = [],
  setSelectedRowKeys,
  setBulkActionVisible,
  selectedStatus,
  setSelectedStatus,
  onBulkStatusChange,
  bulkActionVisible,
  onViewDetails,
  onEditItems,
  onEditDateTime,
  onDelete,
  onCaptureSavedCard,
  onNoteSaved,
  CalendarIcon,
  groupByDate = false,
  showSchedulingWarnings = false
}) => {
  const { hasPermission } = usePermission();

  const formatOnfleetTime = (value) => {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : String(value);
  };


  const canCaptureSavedCard = (record) => (
    Boolean(onCaptureSavedCard)
    && isSavedCardCaptureEligible(record)
  );

  const renderOnfleetCompletion = (record) => {
    const completionDetails = getOrderCompletionDetails(record);
    const completionTime = getOrderCompletionTime(record);
    const deliveryImages = getOnfleetImageUrls(completionDetails);
    const { finalTrackingUrl } = getTrackingInfo(record);
    const hasAnyOnfleetInfo = record.task_id || completionDetails || completionTime || finalTrackingUrl;

    if (!hasAnyOnfleetInfo) {
      return <span className="text-gray-400">N/A</span>;
    }

    const isCompleted = Boolean(completionDetails || completionTime);
    const statusLabel = completionDetails?.success === false
      ? 'Failed'
      : isCompleted
        ? 'Completed'
        : 'Task';

    return (
      <div className="min-w-0 text-xs leading-snug">
        <Tag
          color={completionDetails?.success === false ? 'red' : isCompleted ? 'green' : 'blue'}
          className="m-0"
        >
          {statusLabel}
        </Tag>
        {completionTime && (
          <div className="mt-1 font-medium whitespace-nowrap">{formatOnfleetTime(completionTime)}</div>
        )}
        {record.task_id && (
          <Tooltip title={`Task: ${record.task_id}`}>
            <div className="text-gray-500 truncate max-w-[128px]">Task: {record.task_id}</div>
          </Tooltip>
        )}
        {(deliveryImages.length > 0 || completionDetails || finalTrackingUrl) && (
          <Button
            type="link"
            size="small"
            className="p-0 h-auto text-left text-xs leading-tight whitespace-normal"
            icon={<InfoCircleFilled />}
            onClick={() => showOnfleetCompletionModal(record, { isAdmin })}
          >
            Details{deliveryImages.length > 0 ? ` / Photos (${deliveryImages.length})` : ''}
          </Button>
        )}
      </div>
    );
  };

  /**
   * Enhanced sorting function that prioritizes scheduled orders over unscheduled ones
   * @param {Array} ordersArray - Array of orders to sort
   * @returns {Array} - Sorted orders with scheduled orders first
   */
  const sortOrdersBySchedulingPriority = (ordersArray) => {
    if (!ordersArray || ordersArray.length === 0) return [];

    // Create a copy to avoid mutating the original array
    const ordersCopy = [...ordersArray];

    return ordersCopy.sort((a, b) => {
      const aHasIssue = hasSchedulingIssue(a);
      const bHasIssue = hasSchedulingIssue(b);

      // If one has scheduling issue and other doesn't, prioritize the scheduled one
      if (aHasIssue && !bHasIssue) return 1;  // a goes after b
      if (!aHasIssue && bHasIssue) return -1; // a goes before b

      // If both have same scheduling status, maintain existing sort order
      // Apply the current sort configuration
      if (sortInfo.columnKey && sortInfo.order) {
        let aValue = a[sortInfo.columnKey];
        let bValue = b[sortInfo.columnKey];

        // Handle nested property access for complex fields
        if (sortInfo.columnKey === 'property_date') {
          aValue = a.property_date;
          bValue = b.property_date;
        } else if (sortInfo.columnKey === 'order_total') {
          aValue = parseFloat(a.order_total || 0);
          bValue = parseFloat(b.order_total || 0);
        } else if (sortInfo.columnKey === 'id') {
          aValue = parseInt(a.id || 0);
          bValue = parseInt(b.id || 0);
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortInfo.order === 'ascend' ? 1 : -1;
        if (bValue == null) return sortInfo.order === 'ascend' ? -1 : 1;

        // Apply sort order
        if (sortInfo.order === 'ascend') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      }

      // Default fallback sort by ID descending
      return parseInt(b.id || 0) - parseInt(a.id || 0);
    });
  };

  // Define base columns that are visible to all users
  const baseColumns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => (
        <div className="flex items-center">
          <span className="font-medium">#{id}</span>
          {showSchedulingWarnings && hasSchedulingIssue(record) && (
            <Tooltip title="Not scheduled - Missing task ID" trigger={['hover', 'focus', 'click']}>
              <span
                role="img"
                aria-label={`Scheduling warning for order #${id}: missing task ID`}
                tabIndex={0}
                className="inline-flex items-center ml-1 text-orange-500"
              >
                <WarningOutlined aria-hidden="true" />
              </span>
            </Tooltip>
          )}
        </div>
      ),
      sorter: true,
      width: ORDER_TABLE_COLUMN_WIDTHS.orderId
    },
    {
      title: 'Property',
      key: 'property',
      width: ORDER_TABLE_COLUMN_WIDTHS.property,
      render: (_, record) => (
        <div className="min-w-0 whitespace-normal break-words">
          <div className="flex items-center">
            <HomeOutlined className="mr-1 text-gray-400" />
            <span>{record.order_property?.property_title || 'N/A'}</span>
          </div>
          <div className="text-xs text-gray-500">
            {record.order_property?.apartment_number || ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Address',
      key: 'address',
      render: (_, record) => (
        <div className="min-w-0 whitespace-normal break-words leading-snug">
         {record.order_property?.apartment_number ? `${record.order_property.apartment_number}, ` : ''}{record.order_property?.apartment_address || record.delivery_address_1 || 'N/A'}
          <div className="text-xs text-gray-500">
            {record.order_property?.delivery_city ? `${record.order_property.delivery_city}, ` : ''}
            {record.order_property?.delivery_zip_code || record.delivery_zip_code || ''}
          </div>
        </div>
      ),
      width: ORDER_TABLE_COLUMN_WIDTHS.address
    },
    {
      title: 'Date',
      dataIndex: 'property_date',
      key: 'property_date',
      render: (date) => {
        if (!date) return 'N/A';
        return (
          <div>
            {new Date(date).toLocaleDateString()}
          </div>
        );
      },
      sorter: true,
      width: ORDER_TABLE_COLUMN_WIDTHS.date
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, record) => {
        const { arrivalTime, completionTime } = extractTimeFromOrder(record);
        return arrivalTime ? `${arrivalTime} - ${completionTime || 'N/A'}` : 'N/A';
      },
      width: ORDER_TABLE_COLUMN_WIDTHS.time
    },
    {
      title: 'Total Amount',
      dataIndex: 'order_total',
      key: 'order_total',
      render: (total) => (
        <div className="font-medium">£{formatPrice(total)}</div>
      ),
      sorter: true,
      width: ORDER_TABLE_COLUMN_WIDTHS.total
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (_, record) => (
        <div className="flex flex-col items-start gap-1">
          <OrderStatusCell
            record={record}
            showSchedulingWarnings={showSchedulingWarnings}
          />

        </div>
      ),
      filters: [
        { text: 'Scheduled', value: 'Scheduled' },
        { text: 'En-Route', value: 'En-route' },
        { text: 'Completed', value: 'Completed' },
        { text: 'Collected', value: 'Collected' },
        { text: 'Cancelled', value: 'Cancelled' },
        { text: 'Failed', value: 'Failed' },
        ...(showSchedulingWarnings ? [{ text: 'Not Scheduled', value: 'not_scheduled' }] : [])
      ],
      filterMultiple: false,
      width: ORDER_TABLE_COLUMN_WIDTHS.status
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status) => (
        status === 1 || status === '1' ?
          <Tag color="green" className="m-0 whitespace-nowrap"><DollarOutlined /> Paid</Tag> :
          <Tag color="orange" className="m-0 whitespace-nowrap"><DollarOutlined /> Unpaid</Tag>
      ),
      width: ORDER_TABLE_COLUMN_WIDTHS.payment
    },
    {
      title: 'Onfleet Completion',
      key: 'onfleet_completion',
      render: (_, record) => renderOnfleetCompletion(record),
      width: ORDER_TABLE_COLUMN_WIDTHS.onfleet
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <OrderTaskNoteButton order={record} onSaved={onNoteSaved} />
          <Tooltip title="View Order">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record.id)}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>

          {canCaptureSavedCard(record) && (
            <Tooltip title={getSavedCardActionTooltip(record)}>
              <Button
                type="text"
                icon={<CreditCardOutlined />}
                aria-label={getSavedCardActionLabel(record)}
                onClick={() => onCaptureSavedCard(record.id, record)}
                className="text-blue-500 hover:text-blue-700"
              />
            </Tooltip>
          )}
          
          {hasPermission('edit_time_orders') && (record.order_status === 'Collected' || !blockChangeStatus(record.order_status)) && (
            <>
              <Tooltip title="Edit Times">
                <Button
                  type="text"
                  icon={CalendarIcon && <CalendarIcon />}
                  onClick={() => {
                    const { arrivalTime, completionTime } = extractTimeFromOrder(record);
                    onEditDateTime(record.id, {
                      arrival_time: arrivalTime,
                      completion_time: completionTime,
                      order_date: record.property_date
                    });
                  }}
                  className="text-green-500 hover:text-green-700"
                />
              </Tooltip>
              
              {hasPermission('edit_items_orders') && (record.order_status === 'Collected' || !blockChangeStatus(record.order_status)) && (
                <Tooltip title="Edit Order Items">
                  <Button
                    type="text"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => onEditItems(record.id)}
                    className="text-purple-500 hover:text-purple-700"
                  />
                </Tooltip>
              )}
            </>
          )}
          
          {hasPermission('delete_items_orders') && (
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record.id)}
                className="text-red-500 hover:text-red-700"
              />
            </Tooltip>
          )}
        </Space>
      ),
      width: ORDER_TABLE_COLUMN_WIDTHS.actions,
      fixed: 'right'
    }
  ];

  // Add customer column for admin users only
  const customerColumn = {
    title: 'Customer',
    key: 'customer',
    width: ORDER_TABLE_COLUMN_WIDTHS.customer,
    render: (_, record) => (
      <div className="min-w-0 whitespace-normal break-words leading-snug">
        <div className="flex items-center">
          <UserOutlined className="mr-1 text-gray-400" />
          <span>{record.delivery_first_name || ''} {record.delivery_last_name || ''}</span>
        </div>
        {record.user?.name && (
          <div className="text-xs text-gray-500">
            Account: {record.user.name}
          </div>
        )}
        {record.user?.email && (
          <div className="text-xs text-gray-500 break-all">
            {record.user.email}
          </div>
        )}
      </div>
    ),
    sorter: (a, b) => {
      const nameA = `${a.delivery_first_name || ''} ${a.delivery_last_name || ''}`;
      const nameB = `${b.delivery_first_name || ''} ${b.delivery_last_name || ''}`;
      return nameA.localeCompare(nameB);
    },
  };

  // Dynamically build column list based on user role
  let tableColumns = [...baseColumns];
  
  // Insert customer column after property for admin users
  if (isAdmin) {
    tableColumns.splice(2, 0, customerColumn);
  }

  // Row selection configuration
  const rowSelection = isAdmin ? {
    columnWidth: ORDER_SELECTION_COLUMN_WIDTH,
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      setBulkActionVisible(keys.length > 0);
    }
  } : null;

  const horizontalScrollWidth = tableColumns.reduce(
    (total, column) => total + (Number(column.width) || 0),
    rowSelection ? ORDER_SELECTION_COLUMN_WIDTH : 0
  );

  const getSelectedPaymentSummary = () => {
    const selectedKeys = new Set(selectedRowKeys.map(key => String(key)));

    return orders
      .filter(order => selectedKeys.has(String(order.id)))
      .reduce((summary, order) => {
        const orderTotal = parseFloat(order.order_total || 0);
        const amount = Number.isFinite(orderTotal) ? orderTotal : 0;

        if (parseInt(order.payment_status) === 1) {
          summary.paid += amount;
        } else {
          summary.unpaid += amount;
        }

        return summary;
      }, { paid: 0, unpaid: 0 });
  };

  // Custom pagination configuration
  const getPaginationConfig = () => {
    const totalRecords = pagination.total || orders.length;
    
    return {
      ...pagination,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      pageSizeOptions: ['5', '10', '50', String(totalRecords)],
      showLessItems: true,
      itemRender: (current, type, originalElement) => {
        if (type === 'page') {
          return <a>{current}</a>;
        }
        return originalElement;
      },
    };
  };

  // Enhanced function to group orders by date with scheduling priority
  const getOrdersGroupedByDate = () => {
    if (!groupByDate) return null;
    
    const grouped = {};
    
    orders.forEach(order => {
      let dateKey = 'No Date';
      
      if (order.property_date) {
        dateKey = order.property_date;
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(order);
    });
    
    // Sort dates in descending order and apply scheduling priority within each date group
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return new Date(b) - new Date(a);
      })
      .map(date => ({
        date,
        displayDate: date === 'No Date' ? 'No Date' : formatDisplayDate(date),
        orders: sortOrdersBySchedulingPriority(grouped[date]) // Apply scheduling priority here
      }));
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Render bulk action section
  const renderBulkActions = () => {
    if (!isAdmin || !bulkActionVisible) return null;
    const selectedPaymentSummary = getSelectedPaymentSummary();
    
    return (
      <div className="bg-gray-100 p-4 rounded-lg mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="font-medium mr-2">Bulk Action:</span>
            <span className="mr-2">{selectedRowKeys.length} orders selected</span>
            <Select
              placeholder="Select status"
              style={{ width: 150 }}
              value={selectedStatus}
              onChange={setSelectedStatus}
            >
              <Option value="Scheduled">Scheduled</Option>
              <Option value="En-route">En-route</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Collected">Collected</Option>
              <Option value="Cancelled">Cancelled</Option>
              <Option value="Failed">Failed</Option>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
            <Tag color="green" className="m-0">
              Selected paid: £{formatPrice(selectedPaymentSummary.paid)}
            </Tag>
            <Tag color="orange" className="m-0">
              Selected unpaid: £{formatPrice(selectedPaymentSummary.unpaid)}
            </Tag>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={onBulkStatusChange}
            className="bg-golden-navy"
          >
            Apply
          </Button>
          <Button
            onClick={() => {
              setSelectedRowKeys([]);
              setBulkActionVisible(false);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  // Apply scheduling priority to the main orders list
  const sortedOrders = sortOrdersBySchedulingPriority(orders);

  // Render standard table view
  if (!groupByDate) {
    return (
      <>
        {renderBulkActions()}
        
        <Table
          className="orders-responsive-table"
          rowSelection={rowSelection}
          columns={tableColumns}
          dataSource={sortedOrders} // Use sorted orders here
          rowKey="id"
          pagination={getPaginationConfig()}
          loading={loading}
          onChange={onTableChange}
          scroll={{ x: horizontalScrollWidth }}
          size="small"
          tableLayout="fixed"
          locale={{ emptyText: 'No orders found' }}
        />
      </>
    );
  }
  
  // Render grouped tables with scheduling priority
  const groupedOrders = getOrdersGroupedByDate();
  
  return (
    <div>
      {renderBulkActions()}
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {groupedOrders.map(group => {
            // Count scheduled vs unscheduled orders in this group
            const scheduledCount = group.orders.filter(order => !hasSchedulingIssue(order)).length;
            const unscheduledCount = group.orders.filter(order => hasSchedulingIssue(order)).length;
            
            return (
              <div key={group.date} className="mb-8">
                <h2 className="text-lg sm:text-xl font-bold mb-4 pb-2 border-b flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2" />
                    {group.displayDate} ({group.orders.length} total)
                  </div>
                  {showSchedulingWarnings && unscheduledCount > 0 && (
                    <div className="flex flex-wrap items-center gap-1 text-sm">
                      <Tag color="green" className="mr-1">
                        {scheduledCount} Scheduled
                      </Tag>
                      <Tooltip title={`${unscheduledCount} ${unscheduledCount === 1 ? 'order is' : 'orders are'} not properly scheduled. Missing task ID.`} trigger={['hover', 'focus', 'click']}>
                        <span
                          role="img"
                          aria-label={`${unscheduledCount} ${unscheduledCount === 1 ? 'order' : 'orders'} missing task ID`}
                          tabIndex={0}
                          className="inline-flex items-center gap-1 text-orange-500"
                        >
                          <WarningOutlined aria-hidden="true" />
                          <span aria-hidden="true">{unscheduledCount}</span>
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </h2>
                
                <Table
                  className="orders-responsive-table"
                  rowSelection={rowSelection}
                  columns={tableColumns}
                  dataSource={group.orders} // Already sorted by scheduling priority
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: horizontalScrollWidth }}
                  size="small"
                  tableLayout="fixed"
                  locale={{ emptyText: 'No orders found' }}
                />
              </div>
            );
          })}
          
          {/* Custom pagination for grouped table view */}
          <div className="mt-6 flex justify-center">
            {pagination.total > pagination.pageSize && (
              <div className="flex w-full flex-col justify-center items-stretch gap-2 mt-4 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <Button
                  disabled={pagination.current === 1}
                  onClick={() => {
                    onTableChange({
                      ...pagination,
                      current: pagination.current - 1
                    });
                  }}
                >
                  Previous
                </Button>
                
                <div className="flex items-center justify-center gap-2">
                  <span>Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}</span>
                  <Select
                    value={pagination.pageSize}
                    onChange={(value) => {
                      const newPageSize = value === String(pagination.total) ? pagination.total : parseInt(value);
                      onTableChange({
                        ...pagination,
                        pageSize: newPageSize,
                        current: 1
                      });
                    }}
                    style={{ width: 100 }}
                  >
                    <Option value={5}>5 / page</Option>
                    <Option value={10}>10 / page</Option>
                    <Option value={50}>50 / page</Option>
                    <Option value={pagination.total}>All</Option>
                  </Select>
                </div>
                
                <Button
                  disabled={pagination.current === Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => {
                    onTableChange({
                      ...pagination,
                      current: pagination.current + 1
                    });
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrderTable;
